import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Utility/Button';
import { XIcon } from '@phosphor-icons/react';
import { useModalContext } from './ModalContext';

const Modal: React.FC = () => {
    const { isOpen, config, closeModal } = useModalContext();
    const contentContainerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (isOpen && config) {
            if (config.onContentRendered) {
                config.onContentRendered();
            }
        }
    }, [isOpen, config]);

    useEffect(() => {
        if (!isOpen || !config?.shouldScrollToBottom || !contentContainerRef.current) return;

        const tryScroll = () => {
            contentContainerRef.current?.scrollTo({
                top: contentContainerRef.current.scrollHeight,
                behavior: 'smooth',
            });
        };

        tryScroll();

        const observer = new MutationObserver(() => {
            tryScroll();
        });

        observer.observe(contentContainerRef.current, {
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, [isOpen, config?.shouldScrollToBottom]);

    if (!isOpen || !config) return null;

    const {
        title,
        content,
        size = 'small',
        showCloseButton = true,
        primaryButton,
        secondaryButton,
        customButtons,
        buttonConfigs,
        closeOnBackdropClick = true,
    } = config;

    const getModalSizeClass = () => {
        switch (size) {
            case 'medium':
                return 'max-w-2xl';
            case 'large':
                return 'max-w-6xl';
            case 'full':
                return '!w-[90dvw] h-[80dvh]';
            // case 'small':
            default:
                return 'max-w-lg';
        }
    };

    const handleBackdropClick = () => {
        if (closeOnBackdropClick) {
            closeModal();
        }
    };

    const handlePrimaryClick = () => {
        primaryButton?.onClick();
        primaryButton?.closeOnClick !== false && closeModal();
    };

    const handleSecondaryClick = () => {
        secondaryButton?.onClick();
        secondaryButton?.closeOnClick !== false && closeModal();
    };

    const showButtons = primaryButton || secondaryButton;
    const showCustomButtons = customButtons && customButtons.length > 0;
    const showButtonConfigs = buttonConfigs && buttonConfigs.length > 0;

    const modalContent = (
        <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[2]"
            onClick={handleBackdropClick}
        >
            <div
                className={`bg-modalBackground rounded-lg py-8 mx-3 w-full relative ${getModalSizeClass()}`}
                onClick={(e) => e.stopPropagation()}
            >
                {showCloseButton && (
                    <div className="absolute top-3 right-3">
                        <Button variant="secondaryRound" onClick={closeModal}>
                            <XIcon size={18} />
                        </Button>
                    </div>
                )}

                <h4 className="text-themeswitch-headline text-themeswitch-headlineColor font-normal pl-8 pt-0 mt-0 mb-4 w-[calc(100%-66px)]">
                    {title}
                </h4>

                <div
                    ref={contentContainerRef}
                    className={`${
                        size === 'full' ? 'max-h-[calc(100%-1rem)]' : 'max-h-[calc(var(--app-height)/2)]'
                    } overflow-y-auto px-8`}
                >
                    {typeof content === 'string' ? (
                        <div className="text-gray-500" dangerouslySetInnerHTML={{ __html: content }} />
                    ) : (
                        content
                    )}
                </div>

                {showCustomButtons && (
                    <div className="mt-6 flex justify-end space-x-2 px-8">
                        {customButtons.map((button, index) => (
                            <div key={index} onClick={closeModal}>
                                {button}
                            </div>
                        ))}
                    </div>
                )}

                {showButtonConfigs && !showCustomButtons && (
                    <div className="mt-6 flex justify-end space-x-2 px-8">
                        {buttonConfigs.map((btnConfig, index) => (
                            <Button
                                key={btnConfig.key || index}
                                variant={btnConfig.variant || 'secondary'}
                                onClick={() => {
                                    btnConfig.onClick();
                                    closeModal();
                                }}
                            >
                                {btnConfig.text}
                            </Button>
                        ))}
                    </div>
                )}

                {showButtons && !showCustomButtons && !showButtonConfigs && (
                    <div className="mt-6 flex justify-end space-x-2 px-8">
                        {secondaryButton && (
                            <Button
                                variant={secondaryButton.variant || 'secondaryOutlined'}
                                onClick={handleSecondaryClick}
                            >
                                {secondaryButton.text}
                            </Button>
                        )}
                        {primaryButton && (
                            <Button variant={primaryButton.variant || 'primary'} onClick={handlePrimaryClick}>
                                {primaryButton.text}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default Modal;
